import * as Sentry from '@sentry/react';

// Default values for storage
export const StorageDefaults = {
  usedEmoticons: [] as string[],
  volume: 1,
  emoticons: [] as Emoticon[],
  promptBlockNewAccounts: false,
  notificationReminder: true,
  trustChatLinks: false,
  triedwebrtc: false,
  muted: false,
  contestVoted: false,
  soundOnlyWhenUnfocused: true,
};

// Registry for converters per key
type LegacyConverter<T> = (value: any) => T | undefined;
const converters: Partial<
  Record<keyof typeof StorageDefaults, LegacyConverter<any>>
> = {};

// Registry for type validators
type TypeValidator<T> = (value: any) => boolean;
const validators: Partial<
  Record<keyof typeof StorageDefaults, TypeValidator<any>>
> = {};

/**
 * Provides a type-safe interface for interacting with browser localStorage,
 * including support for default values, legacy value conversion, type validation, and error handling.
 *
 * The `LocalStorageService` class allows storing, retrieving, and removing items
 * from localStorage using predefined keys and types specified in `StorageDefaults`.
 * It automatically falls back to default values when items are missing or invalid,
 * supports registering converter functions to handle legacy data formats, and
 * validates returned values against expected types to prevent type mismatches.
 *
 * All operations are wrapped in error handling, with exceptions reported to Sentry.
 *
 * Usage:
 * - Use `setItem` and `getItem` for type-safe storage access.
 * - Register legacy converters with `registerConverter` to migrate old data formats.
 * - Register type validators with `registerValidator` for frontend validation.
 * - Use `removeItem` and `clear` to manage stored data.
 */
export class LocalStorageService {
  private static getStorage(): Storage | null {
    try {
      return typeof window !== 'undefined' &&
        typeof localStorage !== 'undefined'
        ? localStorage
        : null;
    } catch (e) {
      Sentry.captureException(e);
      return null;
    }
  }

  static convertValue<T extends keyof typeof StorageDefaults>(
    key: T,
    value: any
  ): (typeof StorageDefaults)[T] | null {
    const converter = converters[key];
    if (converter) {
      return converter(value) ?? StorageDefaults[key] ?? null;
    }
    return null;
  }

  static validateValue<T extends keyof typeof StorageDefaults>(
    key: T,
    value: any,
    throwError = false
  ): value is (typeof StorageDefaults)[T] {
    const validator = validators[key];

    if (validator && !validator(value)) {
      if (throwError) {
        throw new Error(
          `Type mismatch for localStorage key "${key}" – expected ${typeof StorageDefaults[
            key
          ]}, got ${typeof value} (${JSON.stringify(value)})`
        );
      }

      return false;
    }

    return true;
  }

  static setItem<T extends keyof typeof StorageDefaults>(
    key: T,
    value: (typeof StorageDefaults)[T]
  ): void {
    const storage = LocalStorageService.getStorage();
    if (!storage) return;
    try {
      this.validateValue(key, value, true);
      storage.setItem(key, JSON.stringify(value));
    } catch (e) {
      Sentry.captureException(e);
      console.error(
        `Error setting localStorage item "${key}", reverting to defaults:`,
        e
      );
      this.removeItem(key);
    }
  }

  static getItem<T extends keyof typeof StorageDefaults>(
    key: T
  ): (typeof StorageDefaults)[T] {
    const storage = LocalStorageService.getStorage();
    if (!storage) return StorageDefaults[key];
    try {
      const item = storage.getItem(key);
      if (!item) return StorageDefaults[key] ?? null;

      let value: any;
      try {
        value = JSON.parse(item);
      } catch {
        value = item;
      }

      // Apply converter if available
      value = this.convertValue(key, value) ?? value;

      // Validate the final value against expected type
      this.validateValue(key, value, true);

      return value as (typeof StorageDefaults)[T];
    } catch (e) {
      Sentry.captureException(e);
      console.error(
        `Error getting localStorage item "${key}", reverting to defaults:`,
        e
      );
      this.removeItem(key);
      return StorageDefaults[key] ?? null;
    }
  }

  static removeItem<T extends keyof typeof StorageDefaults>(key: T): void {
    const storage = LocalStorageService.getStorage();
    if (!storage) return;
    try {
      storage.removeItem(key);
    } catch (e) {
      Sentry.captureException(e);
      console.error(`Error removing localStorage item "${key}":`, e);
    }
  }

  static clear(): void {
    const storage = LocalStorageService.getStorage();
    if (!storage) return;
    try {
      storage.clear();
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error clearing localStorage:', e);
    }
  }

  /**
   * Register a converter function for a specific storage key.
   * The converter will be applied when retrieving values from localStorage.
   *
   * @param key - The storage key to register the converter for
   * @param converter - Function that takes the raw stored value and returns the converted value, or undefined to skip conversion
   */
  static registerConverter<T extends keyof typeof StorageDefaults>(
    key: T,
    converter: LegacyConverter<(typeof StorageDefaults)[T]>
  ): void {
    converters[key] = converter;
  }

  /**
   * Register a type validator function for a specific storage key.
   * The validator will be used to ensure returned values match the expected type.
   *
   * @param key - The storage key to register the validator for
   * @param validator - Function that checks if a value matches the expected type
   */
  static registerValidator<T extends keyof typeof StorageDefaults>(
    key: T,
    validator: TypeValidator<(typeof StorageDefaults)[T]>
  ): void {
    validators[key] = validator;
  }
}

// Register default converters for legacy values
const booleanFn = (value: any) => {
  // value is boolean if JSON.parse returned a boolean previously
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;

  // return undefined so the default value is used
  return undefined;
};

LocalStorageService.registerConverter('promptBlockNewAccounts', booleanFn);
LocalStorageService.registerConverter('trustChatLinks', booleanFn);
LocalStorageService.registerConverter('triedwebrtc', booleanFn);
LocalStorageService.registerConverter('contestVoted', booleanFn);
LocalStorageService.registerConverter('soundOnlyWhenUnfocused', booleanFn);

// Validator for emoticons: must be an array of objects
LocalStorageService.registerValidator('usedEmoticons', (value: any) => {
  return (
    Array.isArray(value) &&
    value.every((emoticon) => typeof emoticon === 'object')
  );
});

// Validator for volume: must be a number between 0 and 1
LocalStorageService.registerValidator('volume', (value: any) => {
  return typeof value === 'number' && value >= 0 && value <= 1;
});

// Validator for soundOnlyWhenUnfocused: must be a boolean
LocalStorageService.registerValidator('soundOnlyWhenUnfocused', (value: any) => {
  return typeof value === 'boolean';
});
